import { useEffect, useState, useCallback, useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  CircularProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import { githubAuthApiRef } from '../../apis';

interface Commit {
  sha: string;
  message: string;
  url: string;
}

interface PushEvent {
  id: string;
  type: string;
  repo: { name: string };
  payload: { commits: Commit[] };
  created_at: string;
}

interface CommitEntry {
  sha: string;
  message: string;
  repo: string;
  url: string;
  date: string;
}

const useStyles = makeStyles(theme => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(6),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  repoChip: {
    marginRight: theme.spacing(1),
    height: 18,
    fontSize: '0.6rem',
  },
  sha: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  secondary: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  openIcon: {
    padding: theme.spacing(0.5),
  },
  message: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  toggleGroup: {
    height: 24,
  },
  toggleButton: {
    fontSize: '0.65rem',
    padding: theme.spacing(0, 1),
    textTransform: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const ago = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

type TimeRange = '24h' | '7d' | '30d' | 'all';

const TIME_RANGE_MS: Record<TimeRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  'all': Infinity,
};

export const RecentCommitsCard = () => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const [allCommits, setAllCommits] = useState<CommitEntry[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const fetchCommits = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const token = await githubAuth.getAccessToken(['repo']);
      const headers = {
        Authorization: `token ${token}`,
        Accept: 'application/vnd.github.cloak-preview+json',
      };

      // Use Search Commits API — no org: qualifier needed (EMU-scoped)
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      const q = encodeURIComponent(
        `author:@me author-date:>${since}`,
      );
      const searchResp = await fetch(
        `https://api.github.com/search/commits?q=${q}&sort=author-date&order=desc&per_page=100`,
        { headers },
      );

      if (searchResp.ok) {
        const data = await searchResp.json();
        const entries: CommitEntry[] = (data.items ?? []).map((item: any) => ({
          sha: item.sha.substring(0, 7),
          message: (item.commit?.message ?? '').split('\n')[0],
          repo:
            item.repository?.name ??
            item.html_url?.split('/')[4] ??
            'unknown',
          url: item.html_url ?? '#',
          date: item.commit?.author?.date ?? item.commit?.committer?.date ?? '',
        }));
        setAllCommits(entries);
        return;
      }

      // Fallback: Events API
      const userResp = await fetch('https://api.github.com/user', { headers });
      if (!userResp.ok) throw new Error(`GitHub API returned ${userResp.status}`);
      const user = await userResp.json();

      const eventsResp = await fetch(
        `https://api.github.com/users/${encodeURIComponent(user.login)}/events?per_page=100`,
        { headers },
      );
      if (!eventsResp.ok) throw new Error(`GitHub API returned ${eventsResp.status}`);
      const events: PushEvent[] = await eventsResp.json();

      const entries: CommitEntry[] = events
        .filter(e => e.type === 'PushEvent')
        .flatMap(e =>
          (e.payload.commits ?? []).map(c => ({
            sha: c.sha.substring(0, 7),
            message: c.message.split('\n')[0],
            repo: e.repo.name.split('/').pop() ?? e.repo.name,
            url: c.url
              .replace('api.github.com/repos', 'github.com')
              .replace('/commits/', '/commit/'),
            date: e.created_at,
          })),
        )
        .slice(0, 100);
      setAllCommits(entries);
    } catch (e: any) {
      setError(e.message ?? 'Could not load commits');
    } finally {
      setLoading(false);
    }
  }, [githubAuth]);

  useEffect(() => {
    fetchCommits();
  }, [fetchCommits]);

  const commits = useMemo(() => {
    const cutoff = Date.now() - TIME_RANGE_MS[timeRange];
    return allCommits.filter(c => new Date(c.date).getTime() >= cutoff);
  }, [allCommits, timeRange]);

  const handleTimeRangeChange = (_: any, value: TimeRange | null) => {
    if (value) setTimeRange(value);
  };

  const content = () => {
    if (loading) {
      return (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      );
    }
    if (error) {
      return <Typography className={classes.empty}>{error}</Typography>;
    }
    if (commits.length === 0) {
      return (
        <Typography className={classes.empty}>
          No recent commits found.
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {commits.slice(0, 10).map((c, i) => (
          <ListItem key={`${c.sha}-${i}`} className={classes.listItem}>
            <ListItemText
              primary={
                <span className={classes.message}>{c.message}</span>
              }
              secondary={
                <span className={classes.secondary}>
                  <Chip
                    label={c.repo}
                    size="small"
                    variant="outlined"
                    className={classes.repoChip}
                  />
                  <code className={classes.sha}>{c.sha}</code>
                  <Typography variant="caption" color="textSecondary">
                    {ago(c.date)}
                  </Typography>
                </span>
              }
            />
            <Tooltip title="Open in GitHub">
              <IconButton
                className={classes.openIcon}
                size="small"
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItem>
        ))}
        {commits.length > 10 && (
          <Typography
            variant="caption"
            style={{ padding: 8, display: 'block' }}
          >
            +{commits.length - 10} more
          </Typography>
        )}
      </List>
    );
  };

  return (
    <InfoCard
      title="Recent Commits"
      subheader={
        commits.length > 0
          ? `${commits.length} recent commit${commits.length === 1 ? '' : 's'}`
          : undefined
      }
      action={
        <div className={classes.header}>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size="small"
            className={classes.toggleGroup}
          >
            <ToggleButton value="24h" className={classes.toggleButton}>
              24h
            </ToggleButton>
            <ToggleButton value="7d" className={classes.toggleButton}>
              7d
            </ToggleButton>
            <ToggleButton value="30d" className={classes.toggleButton}>
              30d
            </ToggleButton>
            <ToggleButton value="all" className={classes.toggleButton}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchCommits} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      }
    >
      {content()}
    </InfoCard>
  );
};
